import * as U from '@unimpaired/utils'
import { isStatuslessAxiosError } from './axios-error.helpers'

/** Bearer-credential query params that must never reach Sentry —
 *  `wlt` is the waitlist magic link, `token` any other one-time link. */
const SENSITIVE_QUERY_PARAMS = ['wlt', 'token'] as const

/** Matches only the param values, and works on relative urls — unlike
 *  `new URL()`, which throws on them. */
const sensitiveParamPattern = new RegExp(
  `([?&](?:${SENSITIVE_QUERY_PARAMS.join('|')})=)[^&#]*`,
  'gi',
)

/** The no-login staff schedule link carries its bearer token as the
 *  last path segment; `:`-prefixed segments are route placeholders. */
const scheduleTokenPattern = /(\/staff\/[^/?#]+\/schedule\/)(?!:)[^/?#]+/g

/** Same path, url-encoded (e.g. inside a `redirect=` value). */
const encodedScheduleTokenPattern =
  /(%2Fstaff%2F[^/?#&%]+%2Fschedule%2F)(?!%3A)[^/?#&%]+/gi

/** True on the no-login staff schedule page (token in the path). */
export const isScheduleLinkPath = (pathname: string): boolean =>
  /^\/staff\/[^/]+\/schedule\/[^/]+/.test(pathname) &&
  !/^\/staff\/[^/]+\/schedule\/:/.test(pathname)

/**
 * Redact bearer credentials in a url: the values of sensitive query
 * params (`wlt`, `token`) and the staff schedule path token, leaving
 * everything else untouched. Returns the input unchanged when it is
 * empty or carries none. Safe for relative urls — never throws the way
 * `new URL(relativePath)` would.
 */
export const redactSensitiveUrlParts = (url?: string | null): string =>
  url
    ? url
        .replace(scheduleTokenPattern, '$1REDACTED')
        .replace(encodedScheduleTokenPattern, '$1REDACTED')
        .replace(sensitiveParamPattern, '$1REDACTED')
    : (url ?? '')

/**
 * Redact every string inside a JSON-safe value (span descriptions,
 * span/trace data urls, headers). Returns a fresh copy.
 */
export const redactDeep = (value: unknown) =>
  JSON.parse(JSON.stringify(value) ?? 'null', (_key, v) =>
    typeof v === 'string' ? redactSensitiveUrlParts(v) : v,
  )

/** False on a schedule-link page: rrweb's href can't be scrubbed. */
export const shouldSampleReplay = (pathname: string) =>
  !isScheduleLinkPath(pathname)

/** Breadcrumb categories whose `data` carries a url. */
const URL_BEARING_BREADCRUMB_CATEGORIES = ['navigation', 'fetch', 'xhr']

const redactStringField = (value: unknown) =>
  typeof value === 'string' ? redactSensitiveUrlParts(value) : value

/** Redact the url-bearing `to`/`from`/`url` fields only. */
export const redactBreadcrumbData = <T extends Record<string, unknown>>(
  data: T,
) => ({
  ...data,
  ...('to' in data && { to: redactStringField(data.to) }),
  ...('from' in data && { from: redactStringField(data.from) }),
  ...('url' in data && { url: redactStringField(data.url) }),
})

/**
 * Strip bearer tokens (the waitlist `wlt`, any `token`, the schedule
 * link) from navigation/fetch/xhr breadcrumb urls before they're stored.
 */
export const redactBreadcrumb = <
  T extends { category?: string; data?: Record<string, unknown> },
>(
  breadcrumb: T,
): T =>
  breadcrumb.data &&
  URL_BEARING_BREADCRUMB_CATEGORIES.includes(breadcrumb.category ?? '')
    ? { ...breadcrumb, data: redactBreadcrumbData(breadcrumb.data) }
    : breadcrumb

/**
 * Shared scrub for error + transaction events: the transaction name
 * and every url-bearing subtree (request, spans, contexts). Generic so
 * it fits both `beforeSend` and `beforeSendTransaction`.
 */
export const redactEventUrls = <
  T extends {
    request?: object
    spans?: unknown[]
    contexts?: object
    transaction?: string
  },
>(
  event: T,
): T => ({
  ...event,
  ...(event.request && { request: redactDeep(event.request) }),
  ...(event.spans && { spans: redactDeep(event.spans) }),
  ...(event.contexts && { contexts: redactDeep(event.contexts) }),
  ...(event.transaction && {
    transaction: redactSensitiveUrlParts(event.transaction),
  }),
})

/**
 * Error signatures injected by software we don't ship — in-app
 * browsers' webview bridges, browser extensions, and
 * link-scanner bots. None of these symbols exist in our source, so
 * dropping them cannot hide a real app error. Fed to Sentry's
 * `ignoreErrors`, which (unlike `beforeSend`) also matches the
 * bare-string promise rejections these sources emit.
 */
export const THIRD_PARTY_NOISE_PATTERNS = [
  // extension / native-bridge string rejection
  /Object Not Found Matching Id/,
  // iOS in-app webview bridge (Instagram et al)
  /window\.webkit\.messageHandlers/,
  // Snapchat's in-app browser, referencing its bridge after teardown
  /SCDynimacBridge/,
  // Android WebView bridge object GC'd mid-call
  /Java object is gone/,
  // Android WebView bridge: host's Java method threw
  /Java exception was raised during method invocation/,
  // iOS host bridge (DuckDuckGo) never sent a reply
  /WKWebView API client did not respond/,
  // WebExtension content-script messaging
  /Invalid call to runtime\.sendMessage/,
  // Snapchat's in-app browser injects this bridge and then throws on it
  /SCDynimacBridge/,
]

/**
 * A request that died before any response arrived — the server never
 * answered (flaky mobile wifi, user navigated away, aborted request).
 * Matches both the raw axios error shape (`isAxiosError`, no `response`)
 * and the app's wrapped api error shape (`isNetworkError` from
 * `describeApiError`). These are client connectivity, not code defects,
 * so `beforeSend` drops them instead of alarming Sentry.
 */
export const isClientNetworkError = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) return false
  // Before .response: axios leaks the XHR there
  if (isStatuslessAxiosError(err)) return true
  if ('response' in err && err.response) return false
  if ('isAxiosError' in err && err.isAxiosError === true) return true
  return 'isNetworkError' in err && err.isNetworkError === true
}

/**
 * WebKit's own IndexedDB failure messages — dropped under memory
 * pressure/backgrounding, or wiped by a user clearing site data
 * (Apple Mail's in-app browser does this on open), or an iOS webview
 * handing Firebase's heartbeat `upgradeneeded` an undefined db. Firebase
 * (Auth + its heartbeat) is the only IndexedDB user and recovers by
 * itself either way.
 */
const WEBKIT_INDEXED_DB_NOISE_MESSAGES = [
  'Connection to Indexed Database server lost',
  'Database deleted by request of the user',
  "createObjectStore')",
]

/**
 * Matches both the raw DOMException and Firebase's wrapped Error,
 * keyed on WebKit's exact messages so an app-owned IndexedDB failure
 * (different message) still reports.
 */
export const isTransientIndexedDbError = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  'message' in err &&
  typeof err.message === 'string' &&
  U.anyIncludes(err.message, WEBKIT_INDEXED_DB_NOISE_MESSAGES)

/**
 * Firebase Auth's own connectivity failure — the SDK could not reach
 * Identity Platform. Its background token refresh raises this as an
 * unhandled rejection with no app frame in the stack, so there is
 * nothing to fix. Same class as `isClientNetworkError`; the login form
 * still shows its own copy through `getAuthErrorMessage`.
 */
export const isFirebaseNetworkError = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  'code' in err &&
  err.code === 'auth/network-request-failed'

/**
 * A request the browser threw away — the user navigated mid-flight, or
 * iOS suspended a backgrounded tab. Arrives as a DOMException named
 * AbortError, or wrapped by the SDK with the name in the message. Our
 * own timeouts abort through axios (`ECONNABORTED`), which
 * `isClientNetworkError` already covers, so nothing app-owned is lost.
 */
export const isClientAbortError = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) return false
  if ('name' in err && err.name === 'AbortError') return true
  return (
    'message' in err &&
    typeof err.message === 'string' &&
    /^(Error: )?AbortError\b/.test(err.message)
  )
}

const CLIENT_NOISE_PREDICATES = [
  isClientNetworkError,
  isTransientIndexedDbError,
  isFirebaseNetworkError,
  isClientAbortError,
]

/**
 * The browser's condition, not our defect — a dropped connection, an
 * aborted request, a platform storage blip. `beforeSend` drops these so
 * a mobile signal drop never reads as a production error.
 */
export const isClientNoise = (err: unknown): boolean =>
  CLIENT_NOISE_PREDICATES.some((matches) => matches(err))
